/**
 * Subscribes the TanStack secondary view to Alive. Runs alongside the Relay subscriptions: the two
 * own different caches, so neither writes over the other. Step one of github/issues#22240.
 */
import {throttle} from '@github/mini-throttle'
import {useQueryClient} from '@github-ui/react-query'
import {useAlive} from '@github-ui/use-alive'
import useIsMounted from '@github-ui/use-is-mounted'
import {memo, useCallback, useMemo} from 'react'

import type {IssueViewerLiveUpdateChannels} from './fetch-issue-viewer-secondary-view'
import {invalidateScopes, invalidateScopesForNested, type InvalidationScope} from './secondary-view-invalidation'

const THROTTLE_MS = 2000

/** `wait` is the server's replication lag: refetching sooner risks reading a stale replica. */
type AliveNotification = {wait?: number}

function runAfterAliveWait(run: () => void, data: AliveNotification | undefined): void {
  window.setTimeout(run, data?.wait ?? 0)
}

/**
 * A notification carries no field-level detail, so a channel must claim every scope it could imply.
 * The issue channel covers the issue record *and* sub-issue membership, hence all three.
 * Under-claiming leaves a query stale once the view is split into per-section queries.
 *
 * It claims `timeline` too: the timeline channel below is only notified for comments, so a label,
 * field, assignee or milestone change announces its timeline row here alone. Safe to refetch on --
 * the row is written in the same transaction as the change and announced after it commits. The
 * "fires ~3s early" warning on `keysForScope` is about the GraphQL subscription path, not this one.
 */
const ISSUE_CHANNEL_SCOPES: readonly InvalidationScope[] = ['metadata', 'header', 'sub-issues', 'timeline']
const STATE_CHANNEL_SCOPES: readonly InvalidationScope[] = ['header']
const SUB_ISSUE_CHANNEL_SCOPES: readonly InvalidationScope[] = ['sub-issues']
/** Comment rows; the only traffic this channel is notified for. */
const TIMELINE_CHANNEL_SCOPES: readonly InvalidationScope[] = ['timeline']

type LiveUpdateProps = {
  channels: IssueViewerLiveUpdateChannels | undefined
  owner: string
  repo: string
  number: number
  issueId: string | undefined
  /** Set by callers whose header and sidebar render from a page payload rather than this query. */
  routeQueryKey?: readonly unknown[]
}

/** Renders nothing. A component, not a hook, because the sub-issue channels need a hook each. */
export function SecondaryViewLiveUpdates({
  channels,
  owner,
  repo,
  number,
  issueId,
  routeQueryKey,
}: LiveUpdateProps): React.JSX.Element | null {
  const queryClient = useQueryClient()
  const isMounted = useIsMounted()

  // Primitives rather than one object prop: a fresh literal each render would rebuild the throttles
  // and make `useAlive` resubscribe every render, dropping events in the gap.
  const coordinates = useMemo(() => ({owner, repo, number}), [owner, repo, number])

  // One throttle per channel, never a shared one taking scopes as an argument: mini-throttle keeps
  // only the most recent call's arguments for its trailing run, so a state -> issue -> state burst
  // would drop the issue event's scopes entirely. And no `.cancel()` cleanup -- that disables a
  // throttle permanently, so StrictMode's double-invoked effects would kill it before first use.
  const invalidateFor = useCallback(
    (scopes: readonly InvalidationScope[]) => {
      if (!isMounted() || !issueId) return
      invalidateScopes({queryClient, scopes, issueId, coordinates, routeQueryKey})
    },
    [isMounted, issueId, queryClient, coordinates, routeQueryKey],
  )

  const throttledIssueInvalidate = useMemo(
    () => throttle(() => invalidateFor(ISSUE_CHANNEL_SCOPES), THROTTLE_MS),
    [invalidateFor],
  )

  const throttledStateInvalidate = useMemo(
    () => throttle(() => invalidateFor(STATE_CHANNEL_SCOPES), THROTTLE_MS),
    [invalidateFor],
  )

  const throttledTimelineInvalidate = useMemo(
    () => throttle(() => invalidateFor(TIMELINE_CHANNEL_SCOPES), THROTTLE_MS),
    [invalidateFor],
  )

  const throttledInvalidateNested = useMemo(
    () =>
      throttle(() => {
        if (!isMounted()) return
        invalidateScopesForNested({queryClient, scopes: SUB_ISSUE_CHANNEL_SCOPES, routeQueryKey})
      }, THROTTLE_MS),
    [isMounted, queryClient, routeQueryKey],
  )

  const handleIssueUpdate = useCallback(
    (data: AliveNotification) => runAfterAliveWait(() => throttledIssueInvalidate(), data),
    [throttledIssueInvalidate],
  )

  const handleStateUpdate = useCallback(
    (data: AliveNotification) => runAfterAliveWait(() => throttledStateInvalidate(), data),
    [throttledStateInvalidate],
  )

  const handleTimelineUpdate = useCallback(
    (data: AliveNotification) => runAfterAliveWait(() => throttledTimelineInvalidate(), data),
    [throttledTimelineInvalidate],
  )

  const handleSubIssueUpdate = useCallback(
    (data: AliveNotification) => runAfterAliveWait(() => throttledInvalidateNested(), data),
    [throttledInvalidateNested],
  )

  useAlive(channels?.updated ?? null, handleIssueUpdate)
  useAlive(channels?.state ?? null, handleStateUpdate)
  useAlive(channels?.timeline ?? null, handleTimelineUpdate)

  const subIssueChannels = useMemo(() => Object.values(channels?.subIssues ?? {}), [channels?.subIssues])

  if (subIssueChannels.length === 0) return null

  return (
    <>
      {subIssueChannels.map(channel => (
        <SubIssueChannelSubscriber key={channel} channel={channel} onUpdate={handleSubIssueUpdate} />
      ))}
    </>
  )
}

// Mapped over up to 100 children, so `memo` per repo React guidance.
const SubIssueChannelSubscriber = memo(function SubIssueChannelSubscriberInner({
  channel,
  onUpdate,
}: {
  channel: string
  onUpdate: (data: AliveNotification) => void
}): null {
  useAlive(channel, onUpdate)
  return null
})
